<template>
  <div class="h-full flex flex-col bg-dark-primary">
    <!-- Header -->
    <div class="flex-shrink-0 px-4 py-3 border-b border-dark">
      <div>
        <h1 class="text-lg font-semibold text-dark-primary">Overview</h1>
        <p class="text-sm text-dark-secondary">Session summary and quick actions</p>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4">

      <!-- Loading state -->
      <div v-if="loading" class="flex items-center justify-center py-16">
        <div class="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>

      <template v-else>

        <!-- AI Security Analysis -->
        <div v-if="verdict">
          <div class="flex items-center gap-2 mb-3">
            <Brain class="w-4 h-4 segura-icon" />
            <h2 class="text-sm font-semibold text-dark-primary uppercase tracking-wide">Summary</h2>
          </div>
          <div
            class="bg-dark-tertiary border border-dark rounded-lg p-3 cursor-pointer transition hover:bg-dark-quaternary"
            @click="isVerdictExpanded = !isVerdictExpanded"
          >
            <p 
              class="text-sm text-dark-primary leading-relaxed"
              :class="{ 'line-clamp-15': !isVerdictExpanded && isVerdictLong }"
            >
              {{ verdict }}
            </p>
            <button 
              v-if="isVerdictLong"
              class="mt-2 text-xs text-accent hover:text-accent/80"
            >
              {{ isVerdictExpanded ? 'Show less' : 'Show more' }}
            </button>
          </div>
        </div>

        <!-- TTPs Detected -->
        <div v-if="ttps && ttps.length > 0">
          <div class="flex items-center gap-2 mb-3">
            <Bug class="w-4 h-4 segura-icon" />
            <h2 class="text-sm font-semibold text-dark-primary uppercase tracking-wide">Threats</h2>
          </div>
          <div class="space-y-1">
            <div v-for="ttp in ttps" :key="ttp.tactic_id" class="border border-dark rounded-lg overflow-hidden">
              <!-- Tactic Header (expandible) -->
              <button
                @click="toggleTactic(ttp.tactic_id)"
                class="w-full flex items-center justify-between px-3 py-2 bg-dark-tertiary hover:bg-dark-quaternary transition text-left"
              >
                <div class="flex items-center gap-2">
                  <ChevronRight 
                    class="w-3 h-3 text-dark-secondary transition-transform" 
                    :class="{ 'rotate-90': expandedTactics.has(ttp.tactic_id) }"
                  />
                  <span class="text-sm font-medium text-dark-primary">{{ ttp.tactic_name }}</span>
                  <span class="px-1.5 py-0.5 text-xs bg-dark-quaternary text-dark-secondary rounded">{{ ttp.techniques.length }}</span>
                </div>
                <span class="text-xs text-error font-mono font-semibold">{{ ttp.tactic_id }}</span>
              </button>
              
              <!-- Techniques (expandido) -->
              <div v-show="expandedTactics.has(ttp.tactic_id)" class="bg-dark-primary">
              <div v-for="technique in ttp.techniques" :key="technique"
                   class="flex items-center justify-between px-6 py-2 hover:bg-dark-tertiary transition border-t border-dark cursor-pointer"
                   @click="navigateToTechnique(extractTechniqueId(technique))">
                  <div class="flex items-center gap-3">
                    <span v-if="extractTechniqueId(technique)"
                          class="text-xs bg-error/10 text-error border border-error/30 rounded px-2 py-1 font-mono font-semibold flex-shrink-0">
                      {{ extractTechniqueId(technique) }}
                    </span>
                    <span class="text-sm text-dark-primary">{{ extractTechniqueName(technique) }}</span>
                  </div>
                  <ChevronRight class="w-3 h-3 text-dark-secondary" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Framework Impact (compact) -->
        <div v-if="frameworkViolations && frameworkViolations.length > 0">
          <div class="flex items-center gap-2 mb-3">
            <FileX class="w-4 h-4 segura-icon" />
            <h2 class="text-sm font-semibold text-dark-primary uppercase tracking-wide">Compliance</h2>
          </div>
          <div class="flex flex-wrap gap-2">
            <button v-for="framework in frameworkViolations" :key="framework.name"
                  class="px-2 py-1 text-xs rounded font-medium transition hover:opacity-80 hover:scale-105 cursor-pointer"
                  :class="getFrameworkStyle(framework.name)"
                  @click="switchTab('compliance')"
            >
              {{ getFrameworkDisplayName(framework.name) }} ({{ framework.violations }})
            </button>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else-if="!verdict && !ttps?.length" class="flex flex-col items-center justify-center py-10 text-center">
          <Brain class="w-10 h-10 text-dark-secondary opacity-40 mb-3" />
          <p class="text-sm text-dark-secondary">Security analysis pending</p>
        </div>

      </template>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { 
  Brain, Clock, Activity, Settings, Zap, Bug, Terminal, MessageSquare, FileX,
  ChevronRight, ChevronDown, AlertTriangle
} from "lucide-vue-next";
import { apiClient } from "../services/apiClient";

const props = defineProps({
  session: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["switch-tab"]);

// State
const loading = ref(true);
const summary = ref(null);
const isVerdictExpanded = ref(false);
const expandedTactics = ref(new Set()); // Track expanded tactics

// Computed
const verdict = computed(() => summary.value?.narrative || null);

const isVerdictLong = computed(() => {
  if (!verdict.value) return false;
  // Aproximadamente 15 linhas considerando ~50 caracteres por linha no painel
  return verdict.value.length > 750;
});

const ttps = computed(() => summary.value?.ttps_summary || []);

const recommendations = computed(() => summary.value?.recommendations || []);

const frameworkViolations = computed(() => {
  if (!summary.value?.detections?.length) return [];

  // Aggregate unique frameworks across all detections
  const frameworkMap = new Map();
  summary.value.detections.forEach(detection => {
    (detection.compliance?.frameworks_violated ?? []).forEach(fw => {
      const name = fw.framework;
      if (!frameworkMap.has(name)) {
        frameworkMap.set(name, 0);
      }
      frameworkMap.set(name, frameworkMap.get(name) + (fw.controls?.length ?? 0));
    });
  });

  return [...frameworkMap.entries()].map(([name, violations]) => ({ name, violations }));
});

// Methods
function switchTab(tab) {
  emit("switch-tab", tab);
}

function navigateToTechnique(techniqueId) {
  // Navigate to Analysis tab with focused technique
  emit("switch-tab", "analysis", { focusedTechnique: techniqueId });
}

function toggleTactic(tacticId) {
  const expanded = expandedTactics.value;
  if (expanded.has(tacticId)) {
    expanded.delete(tacticId);
  } else {
    expanded.add(tacticId);
  }
  // Force reactivity update
  expandedTactics.value = new Set(expanded);
}

function extractTechniqueId(technique) {
  // Se tem ID no formato T1234, extrai ele
  const match = technique.match(/^(T\d{4}(?:\.\d{3})?)/);
  return match ? match[1] : null;
}

function extractTechniqueName(technique) {
  // Remove ID se houver, retorna só o nome
  const match = technique.match(/^T\d{4}(?:\.\d{3})?\s*-\s*(.+)/);
  return match ? match[1] : technique;
}

function getTechniqueIds(techniques) {
  // Extract MITRE technique IDs (e.g., "T1059.001" from "T1059.001 - Command Line Interface")
  if (!techniques || !Array.isArray(techniques)) return [];
  
  return techniques.map(technique => {
    // Look for pattern like "T1234" or "T1234.001" at the beginning
    const match = technique.match(/^(T\d{4}(?:\.\d{3})?)/);
    return match ? match[1] : technique.split(' ')[0];
  }).filter(id => id.startsWith('T'));
}

function getFrameworkStyle(frameworkName) {
  const f = (frameworkName || '').toLowerCase();
  if (f.includes('iso'))                        return 'bg-purple-600 text-white';
  if (f.includes('nist') && f.includes('csf'))  return 'bg-sky-600 text-white';
  if (f.includes('nist'))                       return 'bg-blue-700 text-white';
  if (f.includes('cis'))                        return 'bg-orange-600 text-white';
  if (f.includes('soc'))                        return 'bg-amber-500 text-white';
  if (f.includes('isa') || f.includes('62443')) return 'bg-indigo-600 text-white';
  if (f.includes('d3fend'))                     return 'bg-green-600 text-white';
  if (f.includes('gdpr'))                       return 'bg-teal-600 text-white';
  if (f.includes('pci'))                        return 'bg-orange-700 text-white';
  if (f.includes('hipaa'))                      return 'bg-blue-600 text-white';
  if (f.includes('sox'))                        return 'bg-amber-600 text-white';
  return 'bg-slate-600 text-white';
}

function getFrameworkDisplayName(frameworkName) {
  const abbreviations = {
    'ISO/IEC 27001:2022':                     '27001',
    'ISO 27001':                              '27001',
    'NIST SP 800-53 Rev. 5':                  'NIST',
    'NIST Cybersecurity Framework (CSF) 2.0': 'CSF',
    'CIS Controls v8':                        'CIS',
    'SOC 2 (Trust Service Criteria)':         'SOC 2',
    'ISA/IEC 62443':                          'IEC 62443',
    'MITRE D3FEND':                           'D3FEND',
    'PCI-DSS':                                'PCI',
    'Sarbanes-Oxley':                         'SOX',
  };
  return abbreviations[frameworkName] || frameworkName.split(' ')[0];
}

function getRiskBadgeStyle(risk) {
  const styles = {
    'CRITICAL': 'background-color: #dc2626; color: white; border: 1px solid #b91c1c;',
    'HIGH': 'background-color: #ef4444; color: white; border: 1px solid #dc2626;', 
    'MEDIUM': 'background-color: #f59e0b; color: white; border: 1px solid #d97706;',
    'LOW': 'background-color: #10b981; color: white; border: 1px solid #059669;'
  };
  return styles[risk?.toUpperCase()] || 'background-color: #6b7280; color: white;';
}

function getStatusColor(status) {
  const colors = {
    'completed': 'bg-green-500',
    'processing': 'bg-yellow-500',
    'pending': 'bg-gray-500',
    'failed': 'bg-red-500'
  };
  return colors[status] || 'bg-gray-500';
}

function formatDuration(ms) {
  if (!ms) return "—";
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

function formatDate(timestamp) {
  if (!timestamp) return '—';
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Fetch summary data
async function loadSummary() {
  if (!props.session?.id) return;
  
  loading.value = true;
  try {
    // Include compliance=true to get framework violations per detection
    const result = await apiClient(`/summaries?session_id=${props.session.id}&compliance=true`);
    summary.value = result?.data || result;
  } catch (err) {
    console.error('[OverviewPanel] Failed to load summary:', err);
    summary.value = null;
  } finally {
    loading.value = false;
  }
}

// Watch for session changes
watch(() => props.session?.id, () => {
  if (props.session?.id) {
    loadSummary();
  }
}, { immediate: true });

onMounted(() => {
  if (props.session?.id) {
    loadSummary();
  } else {
    loading.value = false;
  }
});
</script>

<style scoped>
.line-clamp-15 {
  display: -webkit-box;
  -webkit-line-clamp: 15;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.segura-icon {
  color: #006b69;
}
html[data-theme="dark"] .segura-icon,
.dark .segura-icon {
  color: #00d1b5;
}

.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}
.custom-scrollbar::-webkit-scrollbar { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); border-radius: 3px; }
</style>